import { NotificationItem } from '@ps-int/ps-components/src/notifications/slice';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { Artifact, ArtifactRecipient, LoadingArtifactsStatus, RecipientFetchingStatus } from '../fetching/types';

export type SidebarState = {
    selectedSessionId: string | null;
    artifacts: {
        summarizations: Artifact[];
        cloudRecordings: Artifact[];
        localRecordings: Artifact[];
    };
    loadingArtifactsStatus: LoadingArtifactsStatus;
};

export type ArtifactType = keyof SidebarState['artifacts'];

const initialState: SidebarState = {
    selectedSessionId: null,
    artifacts: {
        summarizations: [],
        cloudRecordings: [],
        localRecordings: [],
    },
    loadingArtifactsStatus: null,
};

const conferencesHistorySidebarSlice = createSlice({
    name: 'conferencesHistorySidebar',
    initialState,
    reducers: {
        setSelectedSessionId(state: SidebarState, { payload }: PayloadAction<string | null>) {
            state.selectedSessionId = payload;
        },
        setLoadingArtifactsStatus(state: SidebarState, { payload }: PayloadAction<LoadingArtifactsStatus>) {
            state.loadingArtifactsStatus = payload;
        },
        setArtifacts(
            state: SidebarState,
            {
                payload,
            }: PayloadAction<{
                summarizations: Artifact[];
                cloudRecordings: Artifact[];
                localRecordings: Artifact[];
            }>
        ) {
            state.artifacts = {
                summarizations: payload.summarizations,
                cloudRecordings: payload.cloudRecordings,
                localRecordings: payload.localRecordings.map((artifact) => ({ id: uuidv4(), ...artifact })),
            };
        },
        setArtifactRecipientFetchingStatus(
            state: SidebarState,
            {
                payload: { artifactId, artifactType, status },
            }: PayloadAction<{
                artifactId: string;
                artifactType: ArtifactType;
                status: RecipientFetchingStatus;
            }>
        ) {
            const targetArtifact = state.artifacts[artifactType]?.find((artifact) => artifact.id === artifactId);
            if (!targetArtifact) return;
            targetArtifact.recipientFetchingStatus = status;
        },
        addArtifactRecipients(
            state: SidebarState,
            {
                payload: { artifactId, artifactType, recipients },
            }: PayloadAction<{
                artifactId: string;
                artifactType: ArtifactType;
                recipients: ArtifactRecipient[];
            }>
        ) {
            const targetArtifact = state.artifacts[artifactType]?.find((artifact) => artifact.id === artifactId);
            if (!targetArtifact) return;
            targetArtifact.recipients.push(...recipients);
        },
    },
});

export const copyToCB = createAction<{ value: string; notificationItem: NotificationItem }>(
    'conferencesHistorySidebar/copyToCB'
);
export const downloadReport = createAction('conferencesHistorySidebar/downloadReport');
export const fetchMoreArtifactRecipients = createAction<{
    confShortUrlId: string;
    sessionId: string;
    artifactId?: string;
    offset: number;
    artifactType: ArtifactType;
}>('conferencesHistorySidebar/fetchMoreArtifactRecipients');
export const {
    setSelectedSessionId,
    setLoadingArtifactsStatus,
    setArtifacts,
    setArtifactRecipientFetchingStatus,
    addArtifactRecipients,
} = conferencesHistorySidebarSlice.actions;
export const conferencesHistorySidebarReducer = conferencesHistorySidebarSlice.reducer;
